import type { RecipeTaxonomyVocabulary } from '@mdvip/shared/graphql/generated/graphql';

/**
 * camelCase `RecipesInput`/`RecipeFacetsInput` field name for a vocabulary — also the URL
 * query-param key AND the suffix under `shared:recipes.filters.categories.*`. One string, three
 * uses, kept as a literal union (not `string`) so `t(\`recipes.filters.categories.${fieldId}\`)`
 * type-checks against i18next's generated key union.
 */
export type RecipeFacetFieldId =
  | 'cuisine'
  | 'dietaryRestrictions'
  | 'healthConsiderations'
  | 'mainIngredient'
  | 'mealCourse'
  | 'preparationTechnique'
  | 'seasonOccasion'
  | 'styleThemes'
  | 'typeOfDish';

export interface RecipeFacetGroupMeta {
  fieldId: RecipeFacetFieldId;
}

/**
 * Hand-written, not derived from a SCREAMING_SNAKE→camelCase transform: the backend's own
 * `VOCABULARY_FILTER_KEY` table (api/src/nutrition-content/recipes.resolver.ts) is hand-written
 * too, so a future vocabulary with a non-mechanical name would compile fine there while silently
 * breaking a frontend that assumed an algorithmic conversion. Mirrors that table 1:1.
 */
export const RECIPE_FACET_GROUPS: Record<RecipeTaxonomyVocabulary, RecipeFacetGroupMeta> = {
  CUISINE: { fieldId: 'cuisine' },
  DIETARY_RESTRICTIONS: { fieldId: 'dietaryRestrictions' },
  HEALTH_CONSIDERATIONS: { fieldId: 'healthConsiderations' },
  MAIN_INGREDIENT: { fieldId: 'mainIngredient' },
  MEAL_COURSE: { fieldId: 'mealCourse' },
  PREPARATION_TECHNIQUE: { fieldId: 'preparationTechnique' },
  SEASON_OCCASION: { fieldId: 'seasonOccasion' },
  STYLE_THEMES: { fieldId: 'styleThemes' },
  TYPE_OF_DISH: { fieldId: 'typeOfDish' },
};

/**
 * Canonical accordion display order. `recipeFacets` returns groups in backend enum-declaration
 * order (CUISINE, MEAL_COURSE, DIETARY_RESTRICTIONS, ...) — not a designed display order — so
 * consumers should render in this order rather than the order the query response arrives in.
 */
export const RECIPE_FACET_ORDER: RecipeTaxonomyVocabulary[] = [
  'CUISINE',
  'DIETARY_RESTRICTIONS',
  'HEALTH_CONSIDERATIONS',
  'MAIN_INGREDIENT',
  'MEAL_COURSE',
  'PREPARATION_TECHNIQUE',
  'SEASON_OCCASION',
  'STYLE_THEMES',
  'TYPE_OF_DISH',
];

const FIELD_ID_TO_VOCABULARY: Record<string, RecipeTaxonomyVocabulary> = Object.fromEntries(
  RECIPE_FACET_ORDER.map((vocabulary) => [RECIPE_FACET_GROUPS[vocabulary].fieldId, vocabulary]),
);

/** Reverse lookup — e.g. mapping a `?styleThemes=...` URL param key back to `STYLE_THEMES`. */
export function vocabularyForFieldId(fieldId: string): RecipeTaxonomyVocabulary | undefined {
  return FIELD_ID_TO_VOCABULARY[fieldId];
}
